import TaxReceiptUnavailableReasonEnum from '@/lib/Tax/TaxReceiptUnavailableReasonEnum';
import { addGreekTaxToOrder } from '@/lib/Tax/Countries/Greece';
import { container } from '@/container';
import { langFilter } from '@/vue/filters/langFilter';

export const PrintOrderReceiptMixin = {
    data() {
        return {
            receiptPrinted: this.order.receiptPrinted,
        };
    },

    computed: {
        /**
         * @return {string}
         */
        printReceiptLabel() {
            return langFilter(this.receiptPrinted
                ? 'ORDER_PRINTING.REPRINT_RECEIPT_BTN_TEXT'
                : 'ORDER_PRINTING.PRINT_RECEIPT_BTN_TEXT');
        },

        /**
         * @return {string}
         */
        printTaxReceiptLabel() {
            return langFilter(this.receiptPrinted
                ? 'ORDER_PRINTING.REPRINT_TAX_RECEIPT_BTN_TEXT'
                : 'ORDER_PRINTING.PRINT_TAX_RECEIPT_BTN_TEXT');
        },

        /**
         * @return {string|false} Returns a string for the reason why, or false.
         */
        taxReceiptMandatoryReason() {
            return container.taxManager.mustPrintTaxReceiptForOrder(
                container.userService.getCompany(),
                this.order
            );
        },

        /**
         * @return {string|false} Returns a string for the reason why, or false.
         */
        taxReceiptUnavailableReason() {
            if (!this.taxEnabled) {
                return TaxReceiptUnavailableReasonEnum.NOT_ENABLED;
            }

            if (!this.taxReceiptAvailable) {
                // Assume the device is offline. This is the only reason we have currently.
                if (this.order.ID) {
                    return TaxReceiptUnavailableReasonEnum.OFFLINE_AFTER_SALE;
                }

                return TaxReceiptUnavailableReasonEnum.OFFLINE_DURING_SALE;
            }

            return false;
        },

        taxEnabled() {
            return container.userService.getCompany().taxEnabled;
        },

        taxReceiptAvailable() {
            return this.order.hasDataForTaxReceipt();
        },

        taxReceiptMandatory() {
            return !!this.taxReceiptMandatoryReason;
        },

        /**
         * Enable the button to print a non-tax receipt?
         * This is enabled if:
         * - A tax receipt is not required
         * - Or if a tax receipt is required but the data to print a tax receipt isn't loaded.
         *   This is probably because the device is offline. Allow printing a regular receipt in this case.
         *
         * @return {boolean}
         */
        enableReceiptButton() {
            return !this.taxReceiptMandatory
                || !this.taxReceiptAvailable;
        },

        /**
         * The "Print VAT Receipt" button will always be shown if the company has VAT enabled.
         * We'll control if the button is enabled or not by the enableTaxReceiptButton result.
         * Note that the button label is "VAT" receipt, but we are only checking that tax in
         * general in enabled, not specifically UK VAT, because the button will apply for any tax.
         *
         * @return {boolean}
         */
        showTaxReceiptButton() {
            return this.taxEnabled;
        },

        /**
         * @return {boolean}
         */
        enableTaxReceiptButton() {
            return this.taxReceiptAvailable;
        },
    },

    methods: {
        /**
         * @param {boolean} printTaxReceipt
         *
         * @return {Promise}
         */
        async printReceipt(printTaxReceipt = false) {
            // If the user requested to print a normal receipt but a tax receipt is required
            // show an alert telling them why they can't do that.
            if (!printTaxReceipt && this.taxReceiptMandatoryReason) {
                // Check if a tax receipt is actually available.
                // If not, allow a non-tax receipt to be printed.
                if (this.taxReceiptAvailable) {
                    container.taxManager.showTaxReceiptRequiredAlert(this.taxReceiptMandatoryReason);
                    return;
                }
            }

            if (printTaxReceipt && this.taxReceiptUnavailableReason) {
                container.taxManager.showTaxReceiptUnavailableAlert(this.taxReceiptUnavailableReason);
                return;
            }

            this.isPrinting = true;

            await addGreekTaxToOrder(this.order);

            container.orderReceiptPrinter
                .printOrderReceipt(
                    this.order,
                    this.orderAdjustmentResponse || null,
                    this.originalOrder || null, // If this is a balance payment via order edit pass the original order.
                    printTaxReceipt,
                )
                .finally(() => {
                    this.isPrinting = false;
                });
        },
    },
};
